/**
 * ApClauseRow  (SM-23 / DA-2296; groups + `=` compounds added in SM-24 / DA-2297)
 *
 * The AP cascade: appliance (catalog) → {C,T,S,A} operator → value. Unlike HP, AP
 * keeps its operator letter on write (`AP:<id>:<op>:<value>`), so the target+operator
 * prefix is the same two-step CascadeRow for every role — no read/write branching in
 * the step count, only in what the value control looks like.
 *
 * SM-24 adds the two structural patterns that live *inside* a clause:
 *   - multi-appliance `&`/`|` groups — READ only, since the write path takes a single
 *     integer id; the target step swaps to ApplianceGroupPicker for that role.
 *   - the `A` operator's `=`-compound secondary condition, for whitelist keys the
 *     catalog marks `hasCompound`.
 * The third SM-24 pattern (mutual-exclusivity write fan-out) is not a clause-level
 * concern at all — it's N ordinary C:0 WRITE clauses, so it lives in the dialog.
 *
 * `S` is a free-text scalar, not a catalog picker: SM-4 (the sizes-catalog ticket,
 * bidgely-quill repo) is still To Do, so there is no size vocabulary to source
 * options from yet — noted rather than faked with placeholder values.
 */
import React from 'react'
import { Box, Button, Checkbox, FormControlLabel, IconButton, Radio, RadioGroup, TextField, Typography } from '@mui/material'
import { Plus, Trash2 } from 'lucide-react'
import { SearchableSelect } from 'components/common/SearchableSelect'
import { ApMapperClause, ApplianceAttributeCatalog, MapperCatalog, MapperRole } from 'types/apis/surveyInterfaces'
import { CascadeRow } from './CascadeRow'
import { CascadeStep } from './CascadeStep'
import { CascadeStepDef, CascadeValues, MapperScopeConstraint } from './cascadeTypes'
import { RangeValueEditor } from './RangeValueEditor'
import { ApplianceGroupPicker } from './ApplianceGroupPicker'
import { applianceOptions as buildApplianceOptions } from './apCatalog'

export interface ApClauseRowProps {
  clause: ApMapperClause
  role: MapperRole
  catalog?: MapperCatalog
  loading?: boolean
  /** Forward-guard (DA-2184): pins the appliance TARGET when `scope.applianceId` is set. */
  scope?: MapperScopeConstraint
  onChange: (clause: ApMapperClause) => void
  onRemove: () => void
}

/**
 * SM-24: the `=`-compound's own key → value cascade. Keys and values are the group intersection
 * (commonKeys / commonValuesFor), the same rule as the primary attribute — a compound on a
 * multi-appliance group must be valid on every appliance too.
 */
function compoundSteps(
  commonKeys: string[],
  secondaryKey: string | undefined,
  commonValuesFor: (key: string) => string[],
  isRegexFor: (key: string) => boolean,
): CascadeStepDef[] {
  const regex = secondaryKey ? isRegexFor(secondaryKey) : false
  return [
    {
      key: 'secondaryKey',
      label: 'Also (key)',
      options: commonKeys.map((k) => ({ value: k, label: k })),
      placeholder: 'Select a key',
    },
    {
      key: 'secondaryValue',
      label: 'Value',
      options: !secondaryKey || regex ? [] : commonValuesFor(secondaryKey).map((v) => ({ value: v, label: v })),
      disabledReason: regex ? 'Pattern-matched key — not supported in a compound' : undefined,
      placeholder: 'Select a value',
    },
  ]
}

export const ApClauseRow: React.FC<ApClauseRowProps> = ({ clause, role, catalog, loading, scope, onChange, onRemove }) => {
  const isRead = role === 'READ'
  const applianceId = clause.applianceIds?.[0]
  const appliance = catalog?.appliances.find((a) => a.applianceId === applianceId)
  const hasTarget = (clause.applianceIds?.length ?? 0) > 0
  // Groups are read-only — the write path takes a single integer id (§6). The catalog
  // states that as a rule, so honour it rather than hard-coding "READ only" here.
  const groupsAllowed = catalog?.rules.ap.groupsReadOnly === false || isRead

  const allApplianceOptions = buildApplianceOptions(catalog?.appliances)
  // Forward-guard: a question-level scope restricts the choice's appliance picker to the scoped
  // SET — the author composes an `&`/`|` group from those; operator + value stay free. The scope
  // is the GATE: with no scope set, the choice offers NO appliances (author must scope first),
  // rather than falling back to the entire catalog.
  const scopedIds = scope?.applianceIds
  const applianceOptions = scopedIds?.length
    ? allApplianceOptions.filter((o) => scopedIds.includes(Number(o.value)))
    : []

  const targetStep: CascadeStepDef = {
    key: 'target',
    label: 'Appliance',
    options: applianceOptions,
    placeholder: loading ? 'Loading catalog…' : 'Select an appliance',
  }

  const operatorStep: CascadeStepDef = {
    key: 'operator',
    label: 'Operator',
    options: (catalog?.rules.ap.operators ?? []).map((o) => ({ value: o.code, label: o.label })),
    placeholder: 'Select an operator',
  }

  const cascadeValues: CascadeValues = {
    target: applianceId !== undefined ? String(applianceId) : '',
    operator: clause.operator ?? '',
  }

  // Changing the target or operator invalidates everything downstream of it.
  const clearedValueFields = {
    value: undefined,
    rangeStart: undefined,
    rangeEnd: undefined,
    attributeKey: undefined,
    secondaryKey: undefined,
    secondaryValue: undefined,
  } as const

  const handleCascadeChange = (values: CascadeValues) => {
    const id = values.target ? Number(values.target) : undefined
    onChange({
      ...clause,
      applianceIds: id !== undefined ? [id] : [],
      // Target-only changes clear the operator cascade value (it's downstream) —
      // fall back to the clause's existing operator so it isn't silently unset.
      operator: (values.operator as ApMapperClause['operator']) || clause.operator,
      ...clearedValueFields,
    })
  }

  const handleGroupChange = (applianceIds: number[], groupOperator?: string) => {
    onChange({
      ...clause,
      applianceIds,
      groupOperator: groupOperator as ApMapperClause['groupOperator'],
      ...clearedValueFields,
    })
  }

  const handleOperatorChange = (code: string) => {
    onChange({ ...clause, operator: code as ApMapperClause['operator'], ...clearedValueFields })
  }

  // The serializer keys off groupOperator === 'AND' (anything else joins with '|'), but
  // the catalog's `code` is free-form. Normalize off `separator`, which is unambiguous,
  // so an AND group can't silently serialize as OR if the codes are '&'/'|'.
  const groupOperatorOptions = (catalog?.rules.ap.groupOperators ?? []).map((g) => ({
    value: g.separator === '&' || g.code === 'AND' ? 'AND' : 'OR',
    label: g.label,
  }))

  const negateToggle = isRead && (
    <FormControlLabel
      control={<Checkbox size="small" checked={!!clause.negate} onChange={(e) => onChange({ ...clause, negate: e.target.checked })} />}
      label="NOT"
      sx={{ mt: 0.5 }}
    />
  )

  // DA-2184: on READ the value control is chosen through a "Value type" selector rather than
  // shown unconditionally — Set null / Exact value / Range (Range is offered for the COUNT
  // operator only; T/S/A have no range semantics). The mode is derived from the clause so it
  // survives re-render without extra state: isNull → null; a range bound set → range; a value
  // or attribute key set → exact; otherwise unset (no value control until the author picks).
  const valueMode: '' | 'null' | 'exact' | 'range' = clause.isNull
    ? 'null'
    : clause.rangeStart !== undefined || clause.rangeEnd !== undefined
    ? 'range'
    : clause.value !== undefined || clause.attributeKey !== undefined
    ? 'exact'
    : ''

  const valueTypeOptions = [
    { value: 'null', label: 'Set null' },
    { value: 'exact', label: 'Exact value' },
    ...(clause.operator === 'C' ? [{ value: 'range', label: 'Range' }] : []),
  ]

  // Switching value type clears the other modes' fields so the clause can never carry a stale
  // value AND a range AND a null flag at once (the ambiguity the raw string grammar allowed).
  const handleValueModeChange = (mode: string) => {
    if (mode === 'null') {
      onChange({
        ...clause,
        isNull: true,
        value: undefined,
        rangeStart: undefined,
        rangeEnd: undefined,
        // A keeps its attributeKey so it serializes `KEY-null`; other operators clear it.
        attributeKey: clause.operator === 'A' ? clause.attributeKey : undefined,
        secondaryKey: undefined,
        secondaryValue: undefined,
      })
    } else if (mode === 'range') {
      onChange({
        ...clause,
        isNull: undefined,
        value: undefined,
        // A defined (possibly empty) bound marks range mode before the author types anything.
        rangeStart: clause.rangeStart ?? '',
        rangeEnd: clause.rangeEnd,
        attributeKey: undefined,
        secondaryKey: undefined,
        secondaryValue: undefined,
      })
    } else {
      // exact — a defined (possibly empty) value marks the mode; A also keeps its key cascade.
      onChange({
        ...clause,
        isNull: undefined,
        value: clause.value ?? '',
        rangeStart: undefined,
        rangeEnd: undefined,
        attributeKey: clause.operator === 'A' ? clause.attributeKey : undefined,
        secondaryKey: clause.operator === 'A' ? clause.secondaryKey : undefined,
        secondaryValue: clause.operator === 'A' ? clause.secondaryValue : undefined,
      })
    }
  }

  const renderValueControl = () => {
    // READ is gated by the value-type selector; WRITE/UNSELECT keep the isNull-checkbox gating.
    if (isRead) {
      if (valueMode === '' || valueMode === 'null') return null
    } else if (clause.isNull || !clause.operator) {
      return null
    }

    switch (clause.operator) {
      case 'C':
        // READ 'Range' → the range editor; READ 'Exact value' and every WRITE → a single count.
        return isRead && valueMode === 'range' ? (
          <RangeValueEditor
            rangeOnly
            value={{ value: clause.value, rangeStart: clause.rangeStart, rangeEnd: clause.rangeEnd }}
            onChange={(v) => onChange({ ...clause, ...v })}
          />
        ) : (
          <TextField label="Count" size="small" value={clause.value ?? ''} onChange={(e) => onChange({ ...clause, value: e.target.value })} />
        )

      case 'T': {
        const fuelOptions = (appliance?.fuelType ?? []).map((f) => ({ value: f, label: f }))
        return (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
            {fuelOptions.length > 0 ? (
              <SearchableSelect label="Fuel / Type" value={clause.value ?? ''} onChange={(v) => onChange({ ...clause, value: v })} options={fuelOptions} />
            ) : (
              <TextField
                label="Fuel / Type"
                size="small"
                value={clause.value ?? ''}
                onChange={(e) => onChange({ ...clause, value: e.target.value })}
                helperText="No fuelType list for this appliance — free text"
              />
            )}
            {negateToggle}
          </Box>
        )
      }

      case 'S':
        return (
          <TextField
            label="Size"
            size="small"
            value={clause.value ?? ''}
            onChange={(e) => onChange({ ...clause, value: e.target.value })}
          />
        )

      case 'A': {
        // A group A read (AP:id1|id2:A:KEY-value) applies the SAME KEY-value to every appliance,
        // and the write fans out per appliance — so the key AND value must be valid on ALL the
        // selected appliances. Offer only the INTERSECTION (plus globals), not just the first
        // appliance's attributes, so the author can't build a clause that's invalid for some id.
        const groupIds = clause.applianceIds ?? []
        const allAttrs = catalog?.applianceAttributes ?? []
        const rowsForId = (id: number) => allAttrs.filter((a) => a.applianceId === id || a.applianceId === null)
        // The appliance-specific row wins over a global (null) row when both define the key.
        const rowFor = (id: number, key: string) =>
          allAttrs.find((a) => a.attributeKey === key && a.applianceId === id) ??
          allAttrs.find((a) => a.attributeKey === key && a.applianceId === null)
        const entriesForKey = (key: string) =>
          groupIds.map((id) => rowFor(id, key)).filter(Boolean) as ApplianceAttributeCatalog[]

        const isGroup = groupIds.length > 1
        // Keys present on EVERY selected appliance (order follows the first appliance's rows).
        const commonKeys: string[] = groupIds.length === 0 ? [] :
          rowsForId(groupIds[0]).map((a) => a.attributeKey).filter((k, i, self) => self.indexOf(k) === i)
            .filter((k) => groupIds.every((id) => rowsForId(id).some((a) => a.attributeKey === k)))
        // Values valid on EVERY selected appliance for a key (intersection of each appliance's set).
        const commonValuesFor = (key: string): string[] => {
          const es = entriesForKey(key)
          if (es.length === 0) return []
          return es.map((e) => e.values ?? []).reduce((acc, vs) => acc.filter((v) => vs.includes(v)), [...(es[0].values ?? [])])
        }
        const isRegexFor = (key: string) => entriesForKey(key).some((e) => e.isRegex)

        const selectedEntries = clause.attributeKey ? entriesForKey(clause.attributeKey) : []
        const selectedIsRegex = clause.attributeKey ? isRegexFor(clause.attributeKey) : false
        const selectedCompound = selectedEntries.length > 0 && selectedEntries.every((e) => e.hasCompound)

        const keySteps: CascadeStepDef[] = [
          {
            key: 'attrKey',
            label: 'Attribute key',
            options: commonKeys.map((k) => ({ value: k, label: k })),
            placeholder: isGroup ? 'Common attribute' : 'Select a key',
          },
          {
            key: 'attrValue',
            label: 'Value',
            options: selectedIsRegex ? [] : commonValuesFor(clause.attributeKey ?? '').map((v) => ({ value: v, label: v })),
            disabledReason: selectedIsRegex ? 'Pattern-matched key — use the free-text field below' : undefined,
            placeholder: 'Select a value',
          },
        ]
        const keyValues: CascadeValues = {
          attrKey: clause.attributeKey ?? '',
          attrValue: selectedIsRegex ? '' : clause.value ?? '',
        }
        const handleKeyValueChange = (values: CascadeValues) => {
          onChange({
            ...clause,
            attributeKey: values.attrKey || undefined,
            value: values.attrValue || undefined,
            secondaryKey: undefined,
            secondaryValue: undefined,
          })
        }

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {isGroup && (
              <Typography variant="caption" sx={{ color: commonKeys.length === 0 ? 'warning.main' : 'text.secondary' }}>
                {commonKeys.length === 0
                  ? `No attribute is common to all ${groupIds.length} selected appliances — narrow the appliances or split into separate conditions.`
                  : `Showing only attributes common to all ${groupIds.length} selected appliances.`}
              </Typography>
            )}
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <CascadeRow steps={keySteps} values={keyValues} onChange={handleKeyValueChange} loading={loading} />
              {negateToggle}
            </Box>
            {selectedIsRegex && (
              <TextField
                label="Value (pattern)"
                size="small"
                value={clause.value ?? ''}
                onChange={(e) => onChange({ ...clause, value: e.target.value })}
                helperText={isGroup ? 'Pattern must match on all selected appliances' : undefined}
              />
            )}
            {/* The `=`-compound (a second AND-ed attribute in the same segment, e.g.
                AP:72:A:TYPE-x=run_in_operating_hours-true, §6) is an advanced SMB-only pattern —
                hidden behind an opt-in button so the common single-attribute case stays clean. */}
            {selectedCompound && clause.secondaryKey === undefined && (
              <Button
                size="small"
                startIcon={<Plus size={14} />}
                onClick={() => onChange({ ...clause, secondaryKey: '' })}
                sx={{ alignSelf: 'flex-start', textTransform: 'none' }}
              >
                Add combined condition (AND)
              </Button>
            )}
            {selectedCompound && clause.secondaryKey !== undefined && (
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography variant="body2" sx={{ mt: 1.5, color: 'text.secondary' }}>
                  AND
                </Typography>
                <CascadeRow
                  steps={compoundSteps(commonKeys, clause.secondaryKey, commonValuesFor, isRegexFor)}
                  values={{ secondaryKey: clause.secondaryKey ?? '', secondaryValue: clause.secondaryValue ?? '' }}
                  onChange={(values) =>
                    onChange({
                      ...clause,
                      secondaryKey: values.secondaryKey || undefined,
                      secondaryValue: values.secondaryValue || undefined,
                    })
                  }
                />
                <IconButton
                  size="small"
                  onClick={() => onChange({ ...clause, secondaryKey: undefined, secondaryValue: undefined })}
                  aria-label="Remove combined condition"
                  sx={{ color: 'error.main', mt: 1 }}
                >
                  <Trash2 size={14} />
                </IconButton>
              </Box>
            )}
          </Box>
        )
      }

      default:
        return null
    }
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      {/* Target + operator, and — for READ — the value-type radio sits to the RIGHT of the
          operator (not on its own line below) so a choice consumes less vertical space. */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Box>
          {groupsAllowed ? (
            // Group-capable target: the group picker replaces the target step, and the
            // operator step is gated on it by hand (CascadeRow's own gating only knows
            // about its own steps).
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <ApplianceGroupPicker
                applianceIds={clause.applianceIds ?? []}
                groupOperator={clause.groupOperator}
                applianceOptions={applianceOptions}
                groupOperatorOptions={groupOperatorOptions}
                onChange={handleGroupChange}
                loading={loading}
              />
              <CascadeStep
                step={operatorStep}
                value={clause.operator ?? ''}
                onChange={handleOperatorChange}
                disabled={!hasTarget}
                loading={loading}
              />
            </Box>
          ) : (
            <CascadeRow steps={[targetStep, operatorStep]} values={cascadeValues} onChange={handleCascadeChange} loading={loading} />
          )}
        </Box>
        {isRead && hasTarget && clause.operator && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>Value type</Typography>
            <RadioGroup row aria-label="Value type" value={valueMode} onChange={(e) => handleValueModeChange(e.target.value)}>
              {valueTypeOptions.map((o) => (
                <FormControlLabel
                  key={o.value}
                  value={o.value}
                  control={<Radio size="small" />}
                  label={o.label}
                  sx={{ mr: 1 }}
                />
              ))}
            </RadioGroup>
          </Box>
        )}
        <IconButton size="small" onClick={onRemove} aria-label="Remove read condition" sx={{ color: 'error.main', ml: 'auto' }}>
          <Trash2 size={16} />
        </IconButton>
      </Box>
      {hasTarget && clause.operator && (
        isRead ? (
          // The value control (from the selected value type) renders on its own line below.
          renderValueControl()
        ) : (
          // WRITE / UNSELECT keep the original null checkbox + value control (unchanged).
          <>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  checked={!!clause.isNull}
                  onChange={(e) => onChange({ ...clause, isNull: e.target.checked, value: undefined })}
                />
              }
              label="Set to null (clear)"
            />
            {renderValueControl()}
          </>
        )
      )}
    </Box>
  )
}
